/* MLS-UTILITY-001: uniform "service model" the nav + workspace consume.
   Simple services map 1:1 to a storage section; Utility is a presentation
   layer over utility_accounts + trash (storage/data shape unchanged). */
import { SECTION_SERVICE_TYPE, UTILITY_TAB_LABEL, UTILITY_ENTITY_FIELD, utilityTypeOf, utilitySrcForType, SNAPSHOT_KEYS, LINK_KEYS, entryOffGrid, sourceValidFor, sourceMissing, sourceRequiredMessage } from './mlsServiceTypes';
import { clearFindings, applySourceChange } from './mlsAutoFindings';

/* MLS-SEARCH-FINDINGS-ENHANCEMENT-001: None Found checked → conflicting finding values cleared (entity/contact/manual notes kept) */
const withNoneFound = (section, sec, v) => ({ ...sec, none_found: v, entries: v ? (sec.entries || []).map((e) => clearFindings(section, e)) : (sec.entries || []) });

export const entryHasData = (entry) => Object.values(entry || {}).some((v) => typeof v === 'string' && v.trim());
const hasEntity = (sec, field) => (sec?.entries || []).some((e) => (e?.[field] || '').trim());
/* MLS-UTILITY-WELL-SEPTIC-001: a confirmed Private Well / Septic Tank entry is a valid Utility finding (no entity needed) */
const emptySec = { none_found: false, entries: [] };

const statusOf = (satisfied, count) => (satisfied ? 'complete' : count ? 'in_progress' : 'empty');
// MLS-ENTITY-REQUIRED-001: the authority must be selected even when the result is "None Found"
export const ENTITY_REQUIRED_MSG = 'Select the jurisdiction/entity — required even when None Found';
const entityMissing = (entry, field) => !((entry?.[field] || '').trim());

/* MLS-COND-REQ-001: per-entry conditional requirement (Code Yes → Case # + Type; Permit Open/Expired → Permit # + Type).
   Returns the rule message when the positive result lacks a required identifier, else null. */
export const ruleActive = (def, entry) => !!def.findingRule && def.findingRule.positive.includes(String(entry?.[def.findingRule.flag] || '').trim().toLowerCase());
export const entryIssue = (def, entry) =>
  ruleActive(def, entry) && def.findingRule.required.some((k) => !String(entry?.[k] || '').trim()) ? def.findingRule.message : null;

/* MLS-CARRY-FWD-JUR-001 (+002): a new Code / Permit entry inherits the ENTITY & CONTACT block of the
   active entry (else the last entry that has one): jurisdiction context, entity name, contact snapshot and —
   when directory-linked — the link keys, so it shows the same authority with Change Entity available.
   Findings and notes are never carried (allow-list). Never invents an entity when the source has none. */
const JUR_KEYS = ['service_level', 'service_jurisdiction_name'];
const CARRY_KEYS = (def) => [...JUR_KEYS, def.entityField, ...SNAPSHOT_KEYS, ...LINK_KEYS];
const filled = (v) => (typeof v === 'string' ? v.trim() : v !== null && v !== undefined && v !== '');
const hasContext = (e, def) => [...JUR_KEYS, def.entityField, 'service_entity_id'].some((k) => filled(e?.[k]));
export const entrySeed = (entries, fromIdx, def) => {
  const src = hasContext(entries[fromIdx], def) ? entries[fromIdx] : [...entries].reverse().find((e) => hasContext(e, def));
  return src ? Object.fromEntries(CARRY_KEYS(def).filter((k) => filled(src[k])).map((k) => [k, src[k]])) : {};
};

export const buildSimpleModel = (def, form, update) => {
  const section = form[def.key] || emptySec;
  const stored = section.entries || [];
  const entries = stored.length ? stored : [{}];
  const count = stored.filter(entryHasData).length;
  // None Found only clears the findings — the entity stays mandatory (frontend mirror of validate_mls_search_for_completion)
  const issues = entries.map((e) => (entityMissing(e, def.entityField) ? ENTITY_REQUIRED_MSG : (section.none_found ? null : entryIssue(def, e))));
  const satisfied = hasEntity(section, def.entityField) && issues.every((x) => !x);
  const write = (entries2) => update({ [def.key]: { ...section, entries: entries2 } });
  return {
    key: def.key, def, count, satisfied, status: statusOf(satisfied, count), issues,
    noneFound: !!section.none_found, partialNone: null,
    items: entries.map((entry, i) => ({ entry, def, section: def.key, serviceType: SECTION_SERVICE_TYPE[def.key], noneFound: !!section.none_found, label: `${def.tabLabel} ${i + 1}` })),
    setNoneFound: (v) => update({ [def.key]: withNoneFound(def.key, section, v) }),
    setEntry: (i, e) => { const next = [...entries]; next[i] = e; write(next); },
    add: (fromIdx) => { write([...entries, entrySeed(entries, fromIdx, def)]); return entries.length; },
    remove: (i) => write(entries.filter((_, k) => k !== i)),
    reset: (i) => { const next = [...entries]; next[i] = {}; write(next); },
  };
};

export const buildUtilityModel = (def, form, update, entryDefs, { enforceSource = true } = {}) => {
  const ua = form.utility_accounts || emptySec;
  const tr = form.trash || emptySec;
  const uaE = ua.entries || [];
  const trE = tr.entries || [];
  const stored = [...uaE.map((entry, i) => ({ entry, src: 'utility_accounts', srcIdx: i })), ...trE.map((entry, i) => ({ entry, src: 'trash', srcIdx: i }))];
  const raw = stored.length ? stored : [{ entry: {}, src: 'utility_accounts', srcIdx: 0, placeholder: true }];
  const seen = {};
  const items = raw.map((it) => {
    // MLS-UTILITY-WELL-SEPTIC-001: a brand-new Utility section starts as "Water 1" (stored entries keep their derived type)
    const utype = it.placeholder ? 'water' : utilityTypeOf(it.entry, it.src);
    seen[utype] = (seen[utype] || 0) + 1;
    const sec = it.src === 'trash' ? tr : ua;
    return { ...it, section: it.src, utilityType: utype, def: entryDefs[it.src], serviceType: SECTION_SERVICE_TYPE[it.src], noneFound: !!sec.none_found, label: `${UTILITY_TAB_LABEL[utype]} ${seen[utype]}` };
  });
  const count = stored.filter((it) => entryHasData(it.entry)).length;
  const bothNone = !!ua.none_found && !!tr.none_found;
  // MLS-UTILITY-WELL-SEPTIC-002: every Water / Sewer entry that carries data must have an explicit source selection
  // "data" = anything the processor typed (mirror of backend has_data: type/uid/auto text/source itself never count)
  const META = ['utility_type', 'entry_uid', 'auto_findings', 'utility_source'];
  const typed = (e) => Object.entries(e || {}).some(([k, v]) => !META.includes(k) && typeof v === 'string' && v.trim());
  // completed / locked reports are shown as they were accepted — the rule only applies while the processor can act
  // MLS-ENTITY-REQUIRED-001: public entries need their entity (None Found does not bypass); Private Well / Septic are exempt
  const issues = items.map((it) => {
    if (it.placeholder && !typed(it.entry)) return null;
    if (!entryOffGrid(it.entry) && entityMissing(it.entry, UTILITY_ENTITY_FIELD[it.src])) return ENTITY_REQUIRED_MSG;
    if (enforceSource && it.src === 'utility_accounts' && typed(it.entry) && sourceMissing(it.entry, it.utilityType)) return sourceRequiredMessage(it.utilityType);
    return null;
  });
  // coverage: Water + Sewer (public entity or Private Well / Septic Tank; legacy Water/Sewer covers both) and Trash entity
  const covered = { water: false, sewer: false, trash: false };
  items.forEach((it, i) => {
    if (issues[i] || (it.placeholder && !typed(it.entry))) return;
    const src = (it.entry?.utility_source || '').trim();
    if (src === 'private_well') covered.water = true;
    else if (src === 'septic_tank') covered.sewer = true;
    else if (it.utilityType === 'water_sewer') { covered.water = true; covered.sewer = true; }
    else if (covered[it.utilityType] !== undefined) covered[it.utilityType] = true;
  });
  const missingCoverage = Object.keys(covered).filter((k) => !covered[k]);
  const satisfied = missingCoverage.length === 0 && issues.every((x) => !x);
  const secOf = (src) => (src === 'trash' ? tr : ua);
  const listOf = (src) => (src === 'trash' ? trE : uaE);
  const writeSrc = (src, entries) => ({ [src]: { ...secOf(src), entries } });
  const setEntry = (i, e) => {
    const it = items[i];
    const next = [...listOf(it.src)];
    next[it.srcIdx] = it.placeholder ? { utility_type: it.utilityType, ...e } : e;
    update(writeSrc(it.src, next));
  };
  const add = (type) => {
    const src = utilitySrcForType(type);
    const next = [...listOf(src), { utility_type: type }];
    update(writeSrc(src, next));
    return src === 'utility_accounts' ? uaE.length : uaE.length + trE.length;
  };
  const remove = (i) => { const it = items[i]; if (it.placeholder) return; update(writeSrc(it.src, listOf(it.src).filter((_, k) => k !== it.srcIdx))); };
  const setType = (i, type) => {
    const it = items[i];
    const src = utilitySrcForType(type);
    // a Water Source / Sewer Type that does not belong to the new type is dropped (its auto note removed)
    const base = sourceValidFor(type, it.entry.utility_source) ? it.entry : applySourceChange(it.entry, type, null);
    if (src === it.src) { setEntry(i, { ...base, utility_type: type }); return i; }
    const fromField = UTILITY_ENTITY_FIELD[it.src];
    const toField = UTILITY_ENTITY_FIELD[src];
    const moved = { ...base, utility_type: type, [toField]: it.entry[fromField] || it.entry[toField] || '' };
    delete moved[fromField];
    const fromList = listOf(it.src).filter((_, k) => k !== it.srcIdx);
    const toList = [...listOf(src), moved];
    update({ ...writeSrc(it.src, fromList), ...writeSrc(src, toList) });
    return src === 'utility_accounts' ? toList.length - 1 : fromList.length + toList.length - 1;
  };
  return {
    key: def.key, def, count, satisfied, status: statusOf(satisfied, count), items, issues,
    noneFound: bothNone, partialNone: bothNone ? null : (ua.none_found ? 'Water/Sewer' : tr.none_found ? 'Trash' : null),
    missingCoverage,
    setNoneFound: (v) => update({ utility_accounts: withNoneFound('utility_accounts', ua, v), trash: withNoneFound('trash', tr, v) }),
    setEntry, add, remove, setType,
    setSource: (i, v) => setEntry(i, applySourceChange(items[i].entry, items[i].utilityType, v)),
    reset: (i) => setEntry(i, { utility_type: items[i].utilityType }),
  };
};
